import { Injectable, NgZone, inject } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { MsalService } from '@azure/msal-angular';

import { environment } from '../../environments/environment';
import { loginRequest } from '../auth/auth-config';

export interface AgentTextFrame {
  type: 'agent-text';
  ticketId: string;
  ticketMessageId: string;
  eventSeq: number;
  occurredAt: string;
}

export interface TicketEventFrame {
  type: 'ticket-event';
  eventId: string;
  eventSeq: number;
  ticketSeq: number;
  ticketId: string | null;
  ticketMessageId: string | null;
  eventType: string;
  occurredAt: string;
  visibility: string;
}

export interface ResyncFrame {
  type: 'resync';
  ticketId: string | null;
  currentEventSeq: number;
}

export type SupportStreamFrame = AgentTextFrame | TicketEventFrame | ResyncFrame;

/** Pure parser for format_sse's exact wire shape (bluetitan-support
 * core/frames.py:format_sse — "id: {n}\ndata: {json}\n\n"). Exported standalone
 * so it is unit-testable without a live connection. */
export function parseSseChunk(
  buffer: string,
): { frames: { eventId: number; frame: SupportStreamFrame }[]; remainder: string } {
  const frames: { eventId: number; frame: SupportStreamFrame }[] = [];
  let rest = buffer;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const boundary = rest.indexOf('\n\n');
    if (boundary === -1) break;
    const record = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);
    const idLine = record.split('\n').find((l) => l.startsWith('id: '));
    const dataLine = record.split('\n').find((l) => l.startsWith('data: '));
    if (!idLine || !dataLine) continue;
    frames.push({
      eventId: Number(idLine.slice('id: '.length)),
      frame: JSON.parse(dataLine.slice('data: '.length)),
    });
  }
  return { frames, remainder: rest };
}

@Injectable({ providedIn: 'root' })
export class SupportStreamService {
  private msal = inject(MsalService);
  private zone = inject(NgZone);

  /** Fetch-based SSE reader — native EventSource cannot set an Authorization
   * header, which GET /api/v1/support/stream requires (local JWKS validation,
   * no cookie session). Reconnects with Last-Event-ID on stream error.
   *
   * `ticketId` is the connection's SCOPE, and `null` is a real one, not a
   * degenerate call: routers/stream.py scopes an agent connection that names no
   * ticket_id to that agent's FULL accessible ticket set (its
   * list_streamable_ticket_ids), which is what the queue inbox needs. Naming a
   * ticket narrows both the catch-up and the live-tail registration to that one
   * ticket. A customer principal always resolves to a single ticket server-side
   * either way. */
  connect(ticketId: string | null): Observable<SupportStreamFrame> {
    // Demo/offline mode: no live SSE backend — return an inert stream so the live
    // panel shows its (mocked) thread without a dead-connection retry loop. Guarded by
    // !production so demo mode can NEVER disable the real authenticated stream in a
    // prod build, even if the flag were flipped (codex-review HIGH, 2026-08-10).
    if (environment.useMockData && !environment.production) return EMPTY;
    return new Observable((subscriber) => {
      let lastEventId = 0;
      let stopped = false;
      // Teardown must ABORT the request, not just stop reading it. A flag alone leaves
      // the fetch and its body open, so the HTTP connection is never released. The
      // support API is served over HTTP/1.1, where a browser allows only 6 connections
      // per host: every ticket page left behind 2 open streams (the page's watch plus
      // the live panel) and the inbox 1, so by the second ticket opened, a plain request
      // such as the Resolve PATCH queued behind them and never started. A page reload
      // was the only thing that freed them.
      const abort = new AbortController();

      const run = async () => {
        while (!stopped) {
          try {
            const account = this.msal.instance.getActiveAccount();
            const token = account
              ? (await this.msal.instance.acquireTokenSilent({ ...loginRequest, account })).accessToken
              : null;
            const scopeQuery = ticketId === null ? '' : `?ticket_id=${encodeURIComponent(ticketId)}`;
            const response = await fetch(
              `${environment.apiBaseUrl}/api/v1/support/stream${scopeQuery}`,
              {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  ...(lastEventId ? { 'Last-Event-ID': String(lastEventId) } : {}),
                },
                signal: abort.signal,
              },
            );
            // A non-2xx MUST throw, so it lands in the catch below and pays the 2s
            // backoff. Without this the connect loop is UNTHROTTLED on every error
            // status: a 404/401/422 carries a body, so the `!response.body` guard does
            // not fire, the reader drains the error JSON, `done` breaks the inner loop,
            // and `while (!stopped)` immediately re-fetches — the backoff exists only in
            // the catch. Reachable in normal use: an agent opening a stale or shared link
            // to a ticket outside their queue grants gets a fail-closed 404 from
            // routers/stream.py, and the page then hammers /support/stream for as long as
            // the tab is open. (code-review, PR #1571 — newly load-bearing because the
            // inbox and the ticket page now each open a connection, where previously only
            // the live-panel card did, and only for an accessible ticket.)
            if (!response.ok) throw new Error(`SSE connect failed: ${response.status}`);
            if (!response.body) throw new Error('SSE response had no body');
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (!stopped) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const { frames, remainder } = parseSseChunk(buffer);
              buffer = remainder;
              for (const { eventId, frame } of frames) {
                lastEventId = eventId;
                // Emit inside the Angular zone: this loop runs in a fetch/reader
                // microtask outside Angular's zone, so a consumer that updates a
                // bound field directly off a frame would not trigger change
                // detection (platform SSE rule — PROCESSING_VISUALIZER_PATTERN;
                // code-review, 2026-08-10).
                this.zone.run(() => subscriber.next(frame));
              }
            }
          } catch (err) {
            if (!stopped) {
              await new Promise((resolve) => setTimeout(resolve, 2000)); // reconnect backoff
            }
          }
        }
      };
      run();

      return () => {
        stopped = true;
        abort.abort();
      };
    });
  }
}
